import { DosagePlansEditor } from "../components/intake/DosagePlansEditor";
import { IntakeConditionsEditor } from "../components/intake/IntakeConditionsEditor";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type {
  IntakeProgram,
  IntakeProgramConfig,
  IntakeQuestion,
  IntakeRule,
} from "@bloom/shared";
import api from "../lib/api";
const input = "w-full rounded-lg border border-gray-300 p-2 text-sm";
const button = "rounded-lg border px-3 py-2 text-sm disabled:opacity-50";
const fresh = (): IntakeProgramConfig => ({
  name: "",
  description: "",
  active: false,
  productIds: [],
  questions: [],
  rules: [],
  dosagePlans: [],
});
export default function IntakePrograms() {
  const [programs, setPrograms] = useState<IntakeProgram[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [selected, setSelected] = useState<IntakeProgram>();
  const [config, setConfig] = useState<IntakeProgramConfig>(fresh);
  const savedConfig = useRef(JSON.stringify(fresh()));
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");
  const [ready, setReady] = useState(false);
  useEffect(() => {
    Promise.all([
      api.get<any>("/products/intake-programs"),
      api.getAllProducts(),
    ])
      .then(([p, catalog]) => {
        if (!p.success || !catalog.success)
          throw new Error("Unable to load programs or products");
        setPrograms(p.data);
        setProducts(
          (catalog.data?.products || []).filter(
            (p) => p.isActive && !["supply", "lab"].includes(p.productType),
          ),
        );
        setReady(true);
      })
      .catch((e) => setError(e.message));
  }, []);
  const patch = (changes: Partial<IntakeProgramConfig>) => {
    setConfig((c) => ({ ...c, ...changes }));
    setNotice("");
  };
  const question = (id: string, changes: Partial<IntakeQuestion>) =>
    patch({
      questions: config.questions.map((q) =>
        q.id === id ? { ...q, ...changes } : q,
      ),
    });
  const rule = (id: string, changes: Partial<IntakeRule>) =>
    patch({
      rules: config.rules.map((r) => (r.id === id ? { ...r, ...changes } : r)),
    });
  const choose = (p?: IntakeProgram) => {
    if (
      JSON.stringify(config) !== savedConfig.current &&
      !window.confirm("Discard your unsaved program changes?")
    )
      return;
    setSelected(p);
    const next = p
        ? {
            name: p.name,
            description: p.description,
            active: p.active,
            productIds: [...p.productIds],
            questions: structuredClone(p.questions),
            rules: structuredClone(p.rules),
            dosagePlans: structuredClone(p.dosagePlans || []),
          }
        : fresh();
    setConfig(next);
    savedConfig.current = JSON.stringify(next);
    setError("");
    setNotice("");
  };
  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <Link to="/products" className="text-sm text-blue-700">
        Back to products
      </Link>
      <div>
        <h1 className="text-2xl font-semibold">
          Treatment programs & intake routing
        </h1>
        <p className="mt-2 text-gray-500">
          Define required questions and SKU recommendations. Clinics choose
          entry paths; these rules remain admin-controlled.
        </p>
      </div>
      <div className="grid gap-6 lg:grid-cols-[240px_1fr]">
        <nav aria-label="Treatment programs" className="space-y-2">
          <button className={button} onClick={() => choose()}>
            New program
          </button>
          {programs.map((p) => (
            <button
              key={p.id}
              className={`block w-full rounded-lg border p-3 text-left text-sm ${selected?.id === p.id ? "bg-blue-50 border-blue-500" : "bg-white"}`}
              onClick={() => choose(p)}
            >
              {p.name}
              <span className="block text-xs text-gray-500">
                {p.active ? "Active" : "Paused"} · revision {p.revision}
              </span>
            </button>
          ))}
        </nav>
        <section className="rounded-xl border bg-white p-6 space-y-5">
          {error && (
            <p role="alert" className="text-sm text-red-700">
              {error}
            </p>
          )}
          {notice && (
            <p role="status" className="text-sm text-green-700">
              {notice}
            </p>
          )}
          <fieldset disabled={busy || !ready} className="space-y-5">
            <label className="block text-sm">
              Program name
              <input
                className={input}
                maxLength={120}
                value={config.name}
                onChange={(e) => patch({ name: e.target.value })}
              />
            </label>
            <label className="block text-sm">
              Patient introduction
              <textarea
                className={input}
                maxLength={1000}
                value={config.description}
                onChange={(e) => patch({ description: e.target.value })}
              />
            </label>
            <label className="flex gap-2 text-sm">
              <input
                type="checkbox"
                checked={config.active}
                onChange={(e) => patch({ active: e.target.checked })}
              />
              Active
            </label>
            <p className="text-xs text-gray-500">
              Saving changes applies immediately. Pausing a program prevents
              checkout for its assigned SKUs. A SKU can belong to one program.
            </p>
            <div className="space-y-2">
              <h2 className="font-semibold">Eligible product / dose SKUs</h2>
              <input
                className={input}
                aria-label="Search SKUs"
                placeholder="Search products"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <div className="max-h-56 overflow-auto space-y-2">
                {products
                  .filter((p) =>
                    p.displayName?.toLowerCase().includes(search.toLowerCase()),
                  )
                  .map((p) => (
                    <label key={p._id} className="flex gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={config.productIds.includes(p._id)}
                        onChange={(e) =>
                          patch({
                            productIds: e.target.checked
                              ? [...config.productIds, p._id]
                              : config.productIds.filter((id) => id !== p._id),
                          })
                        }
                      />
                      {p.displayName}
                    </label>
                  ))}
              </div>
            </div>
            <DosagePlansEditor
              plans={config.dosagePlans || []}
              products={products.filter((p) =>
                config.productIds.includes(p._id),
              )}
              onChange={(dosagePlans) => patch({ dosagePlans })}
            />
            <div className="space-y-4">
              <h2 className="font-semibold">Program questions</h2>
              <p className="text-sm text-gray-500">
                These are added to the product's existing qualification
                questions in both entry paths.
              </p>
              {config.questions.map((q, i) => (
                <div key={q.id} className="rounded-lg border p-4 space-y-3">
                  <label className="block text-sm">
                    Question {i + 1}
                    <input
                      className={input}
                      maxLength={500}
                      value={q.prompt}
                      onChange={(e) =>
                        question(q.id, { prompt: e.target.value })
                      }
                    />
                  </label>
                  <label className="block text-sm">
                    Answer type
                    <select
                      className={input}
                      value={q.type}
                      onChange={(e) =>
                        question(q.id, {
                          type: e.target.value as IntakeQuestion["type"],
                          choices:
                            e.target.value === "choice"
                              ? ["Option 1", "Option 2"]
                              : undefined,
                        })
                      }
                    >
                      <option value="boolean">Yes / no</option>
                      <option value="choice">Choice</option>
                      <option value="number">Number</option>
                      <option value="text">Short text</option>
                    </select>
                  </label>
                  {q.type === "choice" && (
                    <label className="block text-sm">
                      Choices (one per line)
                      <textarea
                        className={input}
                        value={q.choices?.join("\n") || ""}
                        onChange={(e) =>
                          question(q.id, {
                            choices: e.target.value.split("\n"),
                          })
                        }
                      />
                    </label>
                  )}
                  {q.type === "number" && (
                    <div className="grid grid-cols-2 gap-3">
                      {(["min", "max"] as const).map((key) => (
                        <label key={key} className="text-sm">
                          {key === "min" ? "Minimum" : "Maximum"}
                          <input
                            className={input}
                            type="number"
                            value={q[key] ?? ""}
                            onChange={(e) =>
                              question(q.id, {
                                [key]:
                                  e.target.value === ""
                                    ? undefined
                                    : Number(e.target.value),
                              })
                            }
                          />
                        </label>
                      ))}
                    </div>
                  )}
                  {!!config.dosagePlans?.length && (
                    <div className="space-y-3">
                      <label className="block text-sm">
                        Ask on treatment path
                        <select
                          className={input}
                          value={q.paths?.length === 1 ? q.paths[0] : ""}
                          onChange={(e) =>
                            question(q.id, {
                              paths: e.target.value
                                ? [e.target.value as "starter" | "continuation"]
                                : undefined,
                            })
                          }
                        >
                          <option value="">Both paths</option>
                          <option value="starter">Starter</option>
                          <option value="continuation">Continuation</option>
                        </select>
                      </label>
                      <label className="block text-sm">
                        Ask after selecting these plans (none = before
                        selection)
                        <select
                          multiple
                          className={input}
                          value={q.planIds || []}
                          onChange={(e) => {
                            const ids = Array.from(
                              e.target.selectedOptions,
                            ).map((o) => o.value);
                            question(q.id, {
                              planIds: ids.length ? ids : undefined,
                            });
                          }}
                        >
                          {config.dosagePlans.map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.name || "Unnamed plan"}
                            </option>
                          ))}
                        </select>
                      </label>
                    </div>
                  )}
                  <div className="space-y-2">
                    <p className="text-sm">
                      Show when all conditions match (none = always)
                    </p>
                    <IntakeConditionsEditor
                      questions={config.questions.slice(0, i)}
                      value={q.when || []}
                      onChange={(when) => question(q.id, { when })}
                    />
                  </div>
                  <label className="flex gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={q.required}
                      onChange={(e) =>
                        question(q.id, { required: e.target.checked })
                      }
                    />
                    Required
                  </label>
                  <button
                    className={button}
                    onClick={() =>
                      patch({
                        questions: config.questions.filter(
                          (v) => v.id !== q.id,
                        ),
                      })
                    }
                  >
                    Remove question
                  </button>
                </div>
              ))}
              <button
                className={button}
                disabled={config.questions.length >= 30}
                onClick={() =>
                  patch({
                    questions: [
                      ...config.questions,
                      {
                        id: "q_" + crypto.randomUUID(),
                        prompt: "",
                        type: "boolean",
                        required: true,
                      },
                    ],
                  })
                }
              >
                Add question
              </button>
            </div>
            <div className="space-y-4">
              <h2 className="font-semibold">Routing rules</h2>
              <p className="text-sm text-gray-500">
                Blocking review rules stop both paths. Flags are saved for
                prescriber review without stopping checkout. Guided entry uses
                the first matching plan rule for the chosen path. Direct entry
                keeps the selected plan.
              </p>
              {config.rules.map((r, index) => (
                <div key={r.id} className="rounded-lg border p-4 space-y-3">
                  <h3 className="font-medium">Rule {index + 1}</h3>
                  <IntakeConditionsEditor
                    questions={config.questions.filter(
                      (q) => r.outcome !== "plan" || !q.planIds?.length,
                    )}
                    value={r.conditions}
                    onChange={(conditions) => rule(r.id, { conditions })}
                  />
                  {!r.conditions.length && (
                    <p className="text-xs text-amber-800">
                      No conditions: this rule matches every completed
                      assessment.
                    </p>
                  )}
                  <label className="block text-sm">
                    Outcome
                    <select
                      className={input}
                      value={r.outcome}
                      onChange={(e) =>
                        rule(r.id, {
                          outcome: e.target.value as IntakeRule["outcome"],
                          productId: undefined,
                          planId: undefined,
                        })
                      }
                    >
                      {!config.dosagePlans?.length && (
                        <option value="sku">Recommend a SKU (legacy)</option>
                      )}
                      {!!config.dosagePlans?.length && (
                        <option value="plan">Recommend a dosage plan</option>
                      )}
                      <option value="flag">
                        Flag for prescriber (continue checkout)
                      </option>
                      <option value="review">
                        Require clinic review (both paths)
                      </option>
                    </select>
                  </label>
                  {r.outcome === "plan" && (
                    <label className="block text-sm">
                      Recommended dosage plan
                      <select
                        className={input}
                        value={r.planId || ""}
                        onChange={(e) => rule(r.id, { planId: e.target.value })}
                      >
                        <option value="">Choose dosage plan</option>
                        {config.dosagePlans?.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name} — {p.doseLabel} ({p.path})
                          </option>
                        ))}
                      </select>
                    </label>
                  )}
                  {(r.outcome === "review" || r.outcome === "flag") &&
                    !!config.dosagePlans?.length && (
                      <label className="block text-sm">
                        Apply to plans (none = all)
                        <select
                          multiple
                          className={input}
                          value={r.planIds || []}
                          onChange={(e) => {
                            const ids = Array.from(
                              e.target.selectedOptions,
                            ).map((o) => o.value);
                            rule(r.id, {
                              planIds: ids.length ? ids : undefined,
                            });
                          }}
                        >
                          {config.dosagePlans.map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.name || "Unnamed plan"}
                            </option>
                          ))}
                        </select>
                      </label>
                    )}
                  {r.outcome === "sku" && (
                    <label className="block text-sm">
                      Recommended SKU
                      <select
                        className={input}
                        value={r.productId || ""}
                        onChange={(e) =>
                          rule(r.id, { productId: e.target.value })
                        }
                      >
                        <option value="">Choose SKU</option>
                        {products
                          .filter((p) => config.productIds.includes(p._id))
                          .map((p) => (
                            <option key={p._id} value={p._id}>
                              {p.displayName}
                            </option>
                          ))}
                      </select>
                    </label>
                  )}
                  <label className="block text-sm">
                    Patient explanation
                    <input
                      className={input}
                      maxLength={500}
                      value={r.message}
                      onChange={(e) => rule(r.id, { message: e.target.value })}
                    />
                  </label>
                  <div className="flex gap-2">
                    <button
                      className={button}
                      disabled={index === 0}
                      onClick={() => {
                        const rules = [...config.rules];
                        [rules[index - 1], rules[index]] = [
                          rules[index],
                          rules[index - 1],
                        ];
                        patch({ rules });
                      }}
                    >
                      Move up
                    </button>
                    <button
                      className={button}
                      onClick={() =>
                        patch({
                          rules: config.rules.filter((v) => v.id !== r.id),
                        })
                      }
                    >
                      Remove rule
                    </button>
                  </div>
                </div>
              ))}
              <button
                className={button}
                disabled={config.rules.length >= 50}
                onClick={() =>
                  patch({
                    rules: [
                      ...config.rules,
                      {
                        id: crypto.randomUUID(),
                        conditions: [],
                        outcome: "review",
                        message: "",
                      },
                    ],
                  })
                }
              >
                Add routing rule
              </button>
            </div>
            <button
              className="rounded-lg bg-blue-600 px-5 py-3 text-white disabled:opacity-50"
              onClick={async () => {
                setBusy(true);
                setError("");
                setNotice("");
                try {
                  const payload = { revision: selected?.revision || 0, config };
                  const res: any = selected
                    ? await api.put(
                        "/products/intake-programs/" + selected.id,
                        payload,
                      )
                    : await api.post("/products/intake-programs", payload);
                  if (!res.success) throw new Error(res.error);
                  setSelected(res.data);
                  savedConfig.current = JSON.stringify(config);
                  setPrograms((current) => [
                    ...current.filter((p) => p.id !== res.data.id),
                    res.data,
                  ]);
                  setNotice(
                    "Program saved. Clinic entry options can now use it.",
                  );
                } catch (e: any) {
                  setError(
                    e.response?.data?.error || e.message || "Unable to save",
                  );
                } finally {
                  setBusy(false);
                }
              }}
            >
              {busy ? "Saving…" : "Save program"}
            </button>
          </fieldset>
        </section>
      </div>
    </div>
  );
}
